import React, { useState, useRef, useCallback, useEffect } from 'react';

const BeforeAfterSlider = ({ 
  beforeSrc, 
  afterSrc, 
  altBefore = "Before restoration",
  altAfter = "After restoration",
  initialPercent = 50 
}) => {
  const [sliderPosition, setSliderPosition] = useState(initialPercent);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Keyboard support
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      setSliderPosition(prev => Math.max(0, prev - 2));
    } else if (e.key === 'ArrowRight') {
      setSliderPosition(prev => Math.min(100, prev + 2));
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-[16/9] max-h-[560px] overflow-hidden rounded-2xl shadow-2xl shadow-black/50 select-none cursor-ew-resize"
      style={{
        border: '1px solid rgba(201, 169, 98, 0.2)',
        boxShadow: '0 0 60px rgba(0,0,0,0.5), 0 0 1px rgba(201, 169, 98, 0.3)'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="slider"
      aria-valuenow={Math.round(sliderPosition)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Before and after image comparison slider"
      data-testid="before-after-slider"
    >
      {/* BEFORE Image (Base Layer - shown on LEFT) */}
      <div className="absolute inset-0">
        <img 
          src={beforeSrc}
          alt={altBefore}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* AFTER Image (Clipped Layer - revealed from RIGHT side) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ 
          clipPath: `inset(0 0 0 ${sliderPosition}%)` 
        }}
      >
        <img 
          src={afterSrc}
          alt={altAfter}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* Slider Handle */}
      <div 
        className="absolute top-0 bottom-0 z-20 pointer-events-none"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Vertical Line */}
        <div 
          className="absolute top-0 bottom-0 w-[2px]"
          style={{
            background: 'linear-gradient(to bottom, rgba(201, 169, 98, 0.3), rgba(201, 169, 98, 1), rgba(201, 169, 98, 0.3))',
            boxShadow: '0 0 10px rgba(201, 169, 98, 0.4)'
          }}
        />
        
        {/* Center Knob */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #C9A962 0%, #A8894D 100%)',
            boxShadow: '0 0 20px rgba(201, 169, 98, 0.5), inset 0 2px 4px rgba(255,255,255,0.2)'
          }}
        >
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #0f1724 0%, #1a2332 100%)',
              border: '2px solid rgba(201, 169, 98, 0.6)'
            }}
          >
            {/* Arrows */}
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-[#C9A962]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
              <svg className="w-3 h-3 text-[#C9A962]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* BEFORE Label */}
      <div className="absolute bottom-5 left-5 z-10">
        <span 
          className="px-3 py-1.5 text-[10px] font-medium tracking-[0.2em] uppercase rounded"
          style={{
            background: 'rgba(15, 23, 36, 0.8)',
            backdropFilter: 'blur(8px)',
            color: 'rgba(201, 169, 98, 0.9)',
            border: '1px solid rgba(201, 169, 98, 0.2)'
          }}
        >
          Before
        </span>
      </div>

      {/* AFTER Label */}
      <div className="absolute bottom-5 right-5 z-10">
        <span 
          className="px-3 py-1.5 text-[10px] font-medium tracking-[0.2em] uppercase rounded"
          style={{
            background: 'rgba(201, 169, 98, 0.9)',
            color: '#0f1724',
            fontWeight: '600'
          }}
        >
          After
        </span>
      </div>

      {/* Subtle vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.3) 100%)'
        }}
      />
    </div>
  );
};

export default BeforeAfterSlider;
